import { motion } from "framer-motion";
import { Store, UtensilsCrossed, Film, Building2, Hotel, PartyPopper, ShoppingBag, GraduationCap } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const cardReveal = {
  hidden: { opacity: 0, y: 30, scale: 0.96 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const industries = [
  { icon: Store, name: "Malls & Retail", desc: "High-footfall retail environments for maximum impulse sales." },
  { icon: UtensilsCrossed, name: "QSR Chains", desc: "Quick-service restaurants with high-volume beverage demand." },
  { icon: Film, name: "Cinemas & Multiplexes", desc: "Entertainment venues with captive audience and premium pricing." },
  { icon: Building2, name: "Corporate Cafeterias", desc: "Workplace refreshment solutions for large enterprises." },
  { icon: Hotel, name: "Hospitality", desc: "Hotels, resorts, and hospitality venues requiring premium beverages." },
  { icon: PartyPopper, name: "Events & Exhibitions", desc: "Temporary and permanent installations for events and expos." },
  { icon: ShoppingBag, name: "Institutional Buyers", desc: "Large-scale institutional procurement and distribution." },
  { icon: GraduationCap, name: "Educational Campuses", desc: "Universities, colleges, and school canteens with high student footfall." },
];

export default function IndustriesSection() {
  return (
    <section id="industries" data-testid="industries-section" className="py-16 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="max-w-3xl mb-16"
        >
          <motion.p variants={fadeUp} className="text-[#C8D616] font-semibold text-sm uppercase tracking-widest mb-3">
            Industries Served
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1D3A] leading-tight mb-6">
            Solutions Across Sectors
          </motion.h2>
          <motion.p variants={fadeUp} className="text-base lg:text-lg text-slate-600 leading-relaxed">
            From retail environments to corporate campuses, our beverage systems serve diverse
            industries with tailored solutions for every setting.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {industries.map((ind, i) => (
            <motion.div
              key={ind.name}
              variants={cardReveal}
              custom={i}
              data-testid={`industry-card-${i}`}
              className="group text-center p-6 lg:p-8 rounded-2xl bg-white border border-slate-100 hover:shadow-xl hover:shadow-blue-500/5 hover:border-[#C8D616]/30 transition-all duration-500"
            >
              <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-5 group-hover:bg-[#0B1D3A] transition-colors duration-500">
                <ind.icon size={22} className="text-[#0B1D3A] group-hover:text-[#C8D616] transition-colors duration-500" />
              </div>
              <h3 className="text-base font-bold text-[#0B1D3A] mb-2">{ind.name}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{ind.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
